import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ksh } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useToast } from "../context/ToastContext";

const TIP_WIDTH = 240;

// Caution icon whose tooltip is portalled to <body> so it isn't clipped by the
// card's overflow:hidden / hover transform. Positioned at the badge on hover/focus.
function CautionBadge({ text }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  const show = () => {
    const r = ref.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, left: Math.max(8, r.right - TIP_WIDTH) });
  };
  const hide = () => setPos(null);

  return (
    <span
      ref={ref}
      className="caution-badge"
      tabIndex={0}
      role="note"
      aria-label={`Precautions: ${text}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <i className="fa-solid fa-triangle-exclamation" />
      {pos &&
        createPortal(
          <span className="caution-tip" style={{ top: pos.top, left: pos.left, width: TIP_WIDTH }}>
            <strong>Precautions:</strong> {text}
          </span>,
          document.body
        )}
    </span>
  );
}

export function ProductImage({ src, name }) {
  const [err, setErr] = useState(false);
  const showImg = src && !err;
  return (
    <div className="product-image">
      {showImg && <img src={src} alt={name} loading="lazy" onError={() => setErr(true)} />}
      {!showImg && (
        <div className="ph" style={{ display: "flex" }}>
          <i className="fa-solid fa-leaf" />
          <span>{name}</span>
        </div>
      )}
    </div>
  );
}

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const toast = useToast();
  const soldOut = product.out_of_stock;
  const [confirming, setConfirming] = useState(false);

  // Adding a product with precautions first asks the customer to confirm.
  const add = () => {
    if (soldOut) return;
    if (product.precautions) {
      setConfirming(true);
      return;
    }
    doAdd();
  };

  const doAdd = () => {
    addItem(product);
    toast(`${product.name} added to cart`);
    setConfirming(false);
  };

  return (
    <div className={`product-card${soldOut ? " sold-out" : ""}`}>
      <div className="product-image-wrap">
        <ProductImage src={product.image_url} name={product.name} />
        {soldOut && <span className="stock-badge">Out of stock</span>}
        {product.precautions && <CautionBadge text={product.precautions} />}
      </div>
      <div className="product-info">
        <h3>{product.name}</h3>
        <p>{product.description}</p>
        {product.prescription && (
          <p className="product-prescription">
            <i className="fa-solid fa-prescription-bottle-medical" /> <strong>How to use:</strong> {product.prescription}
          </p>
        )}
        <div className="product-price">
          Ksh {ksh(product.price)} <small>incl.</small>
        </div>
        <button className="add-btn" onClick={add} disabled={soldOut}>
          <i className={`fa-solid ${soldOut ? "fa-ban" : "fa-plus"}`} /> {soldOut ? "Out of stock" : "Add to cart"}
        </button>
      </div>

      {confirming &&
        createPortal(
          <div className="modal-overlay open" onClick={(e) => e.target === e.currentTarget && setConfirming(false)}>
            <div className="modal caution-modal" role="alertdialog" aria-modal="true" aria-label="Product precautions">
              <div className="caution-modal-head">
                <div className="ic"><i className="fa-solid fa-triangle-exclamation" /></div>
                <h2>Before you add this</h2>
                <p>{product.name} carries the following precautions:</p>
              </div>
              <div className="caution-modal-body">
                <p className="caution-text">{product.precautions}</p>
              </div>
              <div className="caution-modal-actions">
                <button className="btn btn-ghost" onClick={() => setConfirming(false)}>Cancel</button>
                <button className="btn btn-leaf" onClick={doAdd}>
                  <i className="fa-solid fa-check" /> I understand, add to cart
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
